import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import CircularProgress from "@mui/material/CircularProgress";
import { useTheme } from "@material-ui/core/styles";

/**
 * Compact settings row item.
 *
 * Props:
 *  - icon: Lucide icon component
 *  - title: string
 *  - description: string
 *  - value: current value
 *  - onChange: (newValue) => void
 *  - loading: boolean
 *  - options: [{ value, label }]         -> renders a Select
 *  - toggle: true                         -> renders a Switch (value = "enabled"/"disabled")
 *  - divider: false                       -> hide bottom divider
 *  - children: ReactNode                  -> custom control (replaces Select/Switch)
 */
const SettingItem = ({
  icon: Icon,
  title,
  description,
  value,
  onChange,
  loading = false,
  options,
  toggle = false,
  divider = true,
  children,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.type === "dark";

  const isToggle = toggle || (options?.length === 2 &&
    options.every((o) => ["enabled", "disabled"].includes(o.value)));

  const handleToggle = (e) => {
    onChange(e.target.checked ? "enabled" : "disabled");
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { xs: "flex-start", sm: "center" },
        gap: { xs: 1, sm: 2 },
        px: { xs: 2, sm: 3 },
        py: 2,
        minHeight: 64,
        borderBottom: divider
          ? isDark
            ? "1px solid rgba(255,255,255,0.04)"
            : "1px solid #f1f5f9"
          : "none",
        transition: "background 0.15s ease",
        "&:hover": {
          background: isDark
            ? "rgba(91, 124, 255, 0.04)"
            : "rgba(61, 92, 253, 0.02)",
        },
      }}
    >
      {/* Icon */}
      {Icon && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            width: 36,
            height: 36,
            borderRadius: "10px",
            "& svg": {
              color: isDark ? "#B4B4B4" : "#64748b",
            },
          }}
        >
          <Icon size={20} strokeWidth={1.6} />
        </Box>
      )}

      {/* Text */}
      <Box sx={{ flex: 1, minWidth: 0, width: { xs: "100%", sm: "auto" } }}>
        <Typography
          sx={{
            fontSize: "0.9rem",
            fontWeight: 600,
            color: isDark ? "#FAFAFA" : "#1e293b",
            lineHeight: 1.3,
          }}
        >
          {title}
        </Typography>
        {description && (
          <Typography
            sx={{
              fontSize: "0.78rem",
              color: isDark ? "#71717A" : "#94a3b8",
              lineHeight: 1.4,
              mt: 0.25,
            }}
          >
            {description}
          </Typography>
        )}
      </Box>

      {/* Control */}
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          gap: 1,
          width: { xs: "100%", sm: "auto" },
          justifyContent: { xs: isToggle ? "flex-end" : "stretch", sm: "flex-end" },
          mt: { xs: 0.5, sm: 0 },
        }}
      >
        {loading && <CircularProgress size={16} sx={{ mr: 0.5 }} />}

        {children ? (
          children
        ) : isToggle ? (
          <Switch
            checked={value === "enabled"}
            onChange={handleToggle}
            size="small"
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": {
                color: "#5B7CFF",
                "&:hover": {
                  backgroundColor: "rgba(91, 124, 255, 0.08)",
                },
              },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                backgroundColor: "#5B7CFF",
              },
            }}
          />
        ) : options ? (
          <Select
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            size="small"
            variant="outlined"
            sx={{
              minWidth: { xs: 0, sm: 160 },
              width: { xs: "100%", sm: "auto" },
              fontSize: "0.85rem",
              fontWeight: 500,
              borderRadius: "14px",
              backgroundColor: isDark ? "rgba(39, 39, 42, 0.8)" : "#f8fafc",
              cursor: "pointer",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: isDark
                  ? "rgba(91, 124, 255, 0.3)"
                  : "#e2e8f0",
                borderWidth: "1.5px",
                transition: "border-color 0.2s ease",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: isDark
                  ? "rgba(91, 124, 255, 0.7)"
                  : "#3D5CFD",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#5B7CFF",
                borderWidth: "2px",
                boxShadow: isDark
                  ? "0 0 0 3px rgba(91, 124, 255, 0.15)"
                  : "0 0 0 3px rgba(61, 92, 253, 0.1)",
              },
              "& .MuiSelect-select": {
                py: 1,
                color: isDark ? "#FFFFFF" : "#1e293b",
                cursor: "pointer",
              },
              "& .MuiSvgIcon-root": {
                color: isDark ? "#5B7CFF" : "#64748b",
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  backgroundColor: isDark ? "#1C1C1F" : "#ffffff",
                  borderRadius: "12px",
                  border: isDark
                    ? "1px solid rgba(91, 124, 255, 0.3)"
                    : "1px solid #e2e8f0",
                  mt: 0.5,
                  boxShadow: isDark
                    ? "0 8px 24px rgba(0, 0, 0, 0.5)"
                    : "0 4px 12px rgba(0, 0, 0, 0.1)",
                  "& .MuiList-root": {
                    p: 1,
                  },
                  "& .MuiMenuItem-root": {
                    color: isDark ? "#F5F5F5" : "#1e293b",
                    fontSize: "0.875rem",
                    py: 1.25,
                    px: 1.75,
                    borderRadius: "8px",
                    mx: 0.5,
                    my: 0.25,
                    minHeight: "auto",
                    transition: "background-color 0.15s ease",
                    "&:hover": {
                      backgroundColor: isDark
                        ? "rgba(91, 124, 255, 0.15)"
                        : "rgba(61, 92, 253, 0.08)",
                    },
                    "&.Mui-selected": {
                      backgroundColor: isDark
                        ? "rgba(91, 124, 255, 0.25)"
                        : "rgba(61, 92, 253, 0.12)",
                      "&:hover": {
                        backgroundColor: isDark
                          ? "rgba(91, 124, 255, 0.35)"
                          : "rgba(61, 92, 253, 0.16)",
                      },
                    },
                  },
                },
              },
            }}
          >
            {options.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        ) : null}
      </Box>
    </Box>
  );
};

export default SettingItem;
